import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const PlanesAlimentarios: React.FC = () => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState('');
  const [pacientes, setPacientes] = useState<any[]>([]);
  const [fechaInicio, setFechaInicio] = useState<Date | null>(null);
  const [dias, setDias] = useState(8);
  const [toneStyle, setToneStyle] = useState('Casual');
  const [modalPeso, setModalPeso] = useState<{ abierto: boolean, mensaje: string, peso: number, fecha: string } | null>(null);

  React.useEffect(() => {
    fetchPacientes();
  }, []);

  const fetchPacientes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pacientes`);
      setPacientes(response.data);
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
    }
  };

  const handleGenerarPlan = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/pacientes/${selectedPaciente}/generar-plan`,
        {
          dia_inicio: fechaInicio?.toISOString().split('T')[0],
          num_dias: dias,
          tone_style: toneStyle,
        }
      );
      const { plan, peso } = response.data;

      // Si el backend indica que hay que preguntar por el peso, mostrar el modal
      if (peso && peso.requiere_confirmacion) {
        console.debug('[DEBUG FRONTEND] Mostrando modal de confirmación de peso:', peso);
        setModalPeso({
          abierto: true,
          mensaje: peso.mensaje,
          peso: peso.peso,
          fecha: peso.fecha,
        });
        // Aquí NO navegues aún, espera la decisión del usuario
        return;
      }

      // Si no hay peso pendiente, navega normalmente
      navigate(`/pacientes/${selectedPaciente}`);
    } catch (error) {
      console.error('Error al generar plan:', error);
    }
  };

  const handleConfirmarPeso = async (acepta: boolean) => {
    console.debug('[DEBUG FRONTEND] Usuario respondió al modal de peso:', acepta, modalPeso);
    if (modalPeso && acepta) {
      try {
        await axios.post(`${API_BASE_URL}/pacientes/${selectedPaciente}/guardar-peso`, {
          peso: modalPeso.peso,
          fecha: modalPeso.fecha,
        });
        console.debug('[DEBUG FRONTEND] Peso guardado correctamente');
        // Opcional: mostrar un toast de éxito
      } catch (error) {
        // Opcional: mostrar un toast de error
        console.error('Error al guardar el peso:', error);
      }
    }
    setModalPeso(null);
    navigate(`/pacientes/${selectedPaciente}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Planes Alimentarios</Typography>
        <Button
          variant="contained"
          onClick={() => setOpenDialog(true)}
        >
          Generar Nuevo Plan
        </Button>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generar Nuevo Plan Alimentario</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Paciente</InputLabel>
                <Select
                  value={selectedPaciente}
                  onChange={(e) => setSelectedPaciente(e.target.value)}
                >
                  {pacientes.map((paciente) => (
                    <MenuItem
                      key={paciente.nombre}
                      value={paciente.nombre}
                    >
                      {paciente.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Fecha de inicio"
                  value={fechaInicio}
                  onChange={(newValue) => setFechaInicio(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Número de días"
                value={dias}
                onChange={(e) => setDias(Number(e.target.value))}
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estilo de tono</InputLabel>
                <Select
                  value={toneStyle}
                  onChange={(e) => setToneStyle(e.target.value)}
                >
                  <MenuItem value="Casual">Casual</MenuItem>
                  <MenuItem value="Formal">Formal</MenuItem>
                  <MenuItem value="Técnico">Técnico</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleGenerarPlan}
            variant="contained"
            disabled={!selectedPaciente || !fechaInicio}
          >
            Generar Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de peso */}
      <Dialog
        open={!!modalPeso?.abierto}
        onClose={() => handleConfirmarPeso(false)}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 3,
            boxShadow: 8,
            minWidth: 340,
            maxWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 0, pt: 2, fontSize: 20, textAlign: 'center' }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <span role="img" aria-label="balanza">⚖️</span>
            Confirmar nuevo peso
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
              {modalPeso?.peso !== undefined ? Number(modalPeso.peso).toFixed(3) : ''} kg
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Fecha: <b>{modalPeso?.fecha}</b>
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
              {modalPeso?.mensaje}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => handleConfirmarPeso(false)} color="inherit" variant="outlined">No guardar</Button>
          <Button onClick={() => handleConfirmarPeso(true)} variant="contained" color="primary">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {pacientes.map((paciente) => (
          <Grid item xs={12} key={paciente.nombre}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {paciente.nombre}
                </Typography>
                <Grid container spacing={2}>
                  {paciente.planes_alimentarios?.map((plan: any, index: number) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1">
                            Plan del {new Date(plan.fecha_creacion).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">Días: {plan.dias}</Typography>
                          <Typography variant="body2">
                            Estado: {plan.modificado ? 'Modificado' : 'Original'}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => navigate(`/planes/${plan.id}`)}
                          >
                            Ver Plan
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PlanesAlimentarios; 